import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { gmbApi } from '@/services/gmbApi';

function getUserId() {
  return localStorage.getItem('user_id') || '';
}

// ── Governance Overview ───────────────────────────────────────────────

export function useGmbGovernanceOverview() {
  const userId = getUserId();
  return useQuery({
    queryKey: ['gmb', 'governance', 'overview', userId],
    queryFn: () => gmbApi.getGovernanceOverview(userId),
    enabled: !!userId,
    staleTime: 5 * 60 * 1000,
  });
}

// ── Live Preview ──────────────────────────────────────────────────────

export function useGmbLivePreview() {
  const userId = getUserId();
  return useQuery({
    queryKey: ['gmb', 'governance', 'live-preview', userId],
    queryFn: () => gmbApi.getLivePreview(userId),
    enabled: !!userId,
    staleTime: 5 * 60 * 1000,
  });
}

// ── Publishing Status ─────────────────────────────────────────────────

export function useGmbPublishingStatus() {
  const userId = getUserId();
  return useQuery({
    queryKey: ['gmb', 'governance', 'publishing-status', userId],
    queryFn: () => gmbApi.getPublishingStatus(userId),
    enabled: !!userId,
    staleTime: 5 * 60 * 1000,
  });
}

// ── Activity Log ──────────────────────────────────────────────────────

export function useGmbActivityLog(params: {
  page?: number;
  pageSize?: number;
  timeFilter?: string;
  actionFilter?: string;
  statusFilter?: string;
} = {}) {
  const userId = getUserId();
  return useQuery({
    queryKey: ['gmb', 'governance', 'activity-log', userId, params],
    queryFn: () => gmbApi.getActivityLog(userId, params),
    enabled: !!userId,
  });
}

export function useDownloadActivityLogCsv() {
  const userId = getUserId();
  return useMutation({
    mutationFn: () => gmbApi.downloadActivityLogCsv(userId),
  });
}

// ── Alerts ────────────────────────────────────────────────────────────

export function useGmbAlerts(severity: string = 'all') {
  const userId = getUserId();
  return useQuery({
    queryKey: ['gmb', 'governance', 'alerts', userId, severity],
    queryFn: () => gmbApi.getAlerts(userId, severity),
    enabled: !!userId,
  });
}

export function useAcknowledgeGmbAlert() {
  const queryClient = useQueryClient();
  const userId = getUserId();

  return useMutation({
    mutationFn: (alertId: string) => gmbApi.acknowledgeAlert(userId, alertId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['gmb', 'governance', 'alerts', userId] });
      queryClient.invalidateQueries({ queryKey: ['gmb', 'governance', 'overview', userId] });
    },
  });
}

export function useRunGmbAlertChecks() {
  const queryClient = useQueryClient();
  const userId = getUserId();

  return useMutation({
    mutationFn: () => gmbApi.runAlertChecks(userId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['gmb', 'governance', 'alerts', userId] });
      queryClient.invalidateQueries({ queryKey: ['gmb', 'governance', 'overview', userId] });
    },
  });
}